import React from "react";
import { EPI_QUESTIONS_CHANGED_EVENT, fetchEpiQuestions, resolveEpiQuestion } from "../lib/assistantApi.js";
import { DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS } from "../lib/dispatchCaptureArrivalHighlight.js";
import { formatPublicationDate } from "../lib/productUpdatesApi.js";
import { formatCaptureActivityGroupLabel, groupCaptureItemsByActivityDate } from "../utils/dispatchCaptureListGroups.js";
import EpiAvatar from "./epi/EpiAvatar.jsx";
import { EpiDownloads } from "./epi/EpiDocument.jsx";
import { describeEpiContext, describeEpiUsage } from "./epi/epiAnswerBlocks.js";
import { ImportContextItem } from "./SollioUniagOrderImportHistory.jsx";
import "./epi/epi.css";

const BUTTON = "rounded-[4px] border border-slate-300 bg-white px-3 py-1 text-sm font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const ACTIVE_BUTTON = "rounded-[4px] border border-accent bg-accent px-3 py-1 text-sm font-semibold text-white";
const SURFACE = "grid min-w-0 gap-2 rounded-[4px] border border-slate-200 bg-white px-4 py-3";
const BADGE = "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.06em]";
const GREEN = `${BADGE} border border-[var(--color-border-badge-subtle)] bg-[var(--grain-badge-bg)] text-[var(--grain-badge-text)]`;
const AMBER = `${BADGE} border border-[var(--color-border-warning-subtle)] bg-amber-soft text-[var(--color-status-warning-ink)]`;
const NEUTRAL = `${BADGE} border border-[var(--color-border-neutral-subtle)] bg-neutral-soft text-muted-strong`;
const STATUS_LABELS = { answered: "Répondue", unknown: "Sans réponse", filtered: "Réponse bloquée", failed: "Aide indisponible" };
const PAGE_SIZE = 25;

function contextText(context) {
  const label = describeEpiContext(context);
  return label ? `${label.prefix} ${label.value}${label.suffix}` : "Non précisé";
}

export function EpiQuestionCard({ item, onResolve, resolving = false }) {
  // Aperçu sur une ligne : le gras de la fenêtre de Lumi n'y est pas rendu.
  const text = (item.answer || "").replaceAll("**", "");
  const answer = item.sources?.length
    ? `${text} · Source : ${item.sources.map((source) => `Guide, ${source.title}`).join(" ; ")}`
    : text || "Aucune réponse.";
  return (
    <article className={SURFACE} data-epi-question-card="true">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <EpiAvatar size={20} />
          <h3 className="m-0 min-w-0 break-words text-[0.86rem] font-bold text-slate-950">{item.question}</h3>
          <time className="text-[0.72rem] text-slate-600" dateTime={item.created_at}>{formatPublicationDate(item.created_at)}</time>
        </div>
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-1">
          {item.needs_review ? <span className={AMBER}>À traiter</span> : item.resolved_at ? <span className={NEUTRAL}>Traitée</span> : null}
          <span className={item.status === "answered" ? GREEN : NEUTRAL}>{STATUS_LABELS[item.status] || item.status}</span>
          {item.feedback === "up" ? <span className={GREEN}>👍 Utile</span> : item.feedback === "down" ? <span className={AMBER}>👎 Pas utile</span> : null}
          {item.needs_review ? (
            <button className={BUTTON} disabled={resolving} onClick={() => onResolve(item.id)} type="button">Marquer traitée</button>
          ) : null}
        </div>
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,2.2fr)]">
        <ImportContextItem label="Utilisateur" value={item.user_label} wrap />
        <ImportContextItem label="Contexte" value={contextText(item.context)} wrap />
        <ImportContextItem label="Réponse de Lumi" value={answer} wrap />
      </div>
      {item.has_document ? <EpiDownloads questionId={item.id} /> : null}
    </article>
  );
}

export default function EpiQuestionHistory() {
  const [view, setView] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [history, setHistory] = React.useState(null);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [resolvingId, setResolvingId] = React.useState("");
  const sequence = React.useRef(0);
  const load = React.useCallback(async ({ quiet = false } = {}) => {
    const request = ++sequence.current;
    if (!quiet) { setLoading(true); setError(""); }
    try {
      const result = await fetchEpiQuestions({ view, page, pageSize: PAGE_SIZE });
      if (request === sequence.current) { setHistory({ ...result, view }); setError(""); }
    } catch (failure) {
      if (request === sequence.current && !quiet) setError(failure.message || "Impossible de charger les questions posées à Lumi.");
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, [page, view]);
  React.useEffect(() => {
    void load();
    const refresh = () => { if (document.visibilityState !== "hidden") void load({ quiet: true }); };
    const timer = window.setInterval(refresh, DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS);
    window.addEventListener(EPI_QUESTIONS_CHANGED_EVENT, refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      ++sequence.current;
      window.clearInterval(timer);
      window.removeEventListener(EPI_QUESTIONS_CHANGED_EVENT, refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);
  const resolve = async (id) => {
    setResolvingId(id);
    try {
      await resolveEpiQuestion(id);
      await load();
    } catch (failure) {
      setError(failure.message || "Impossible de marquer cette question comme traitée.");
    } finally {
      setResolvingId("");
    }
  };
  const items = history?.page === page && history?.view === view ? history.items || [] : [];
  const groups = groupCaptureItemsByActivityDate(items, (item) => item.created_at);
  const total = history?.view === view ? history?.total || 0 : 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const usage = describeEpiUsage(history?.usage);
  const selectView = (nextView) => { setView(nextView); setPage(1); };
  return (
    <div aria-busy={loading} className="grid min-w-0 gap-3" data-epi-question-history="true">
      <section className={SURFACE}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="m-0 text-[0.86rem] font-bold text-slate-950">Robot · {total} question{total > 1 ? "s" : ""}</h2>
          <div aria-label="Filtre des questions" className="flex gap-1.5" role="group">
            <button aria-pressed={view === "all"} className={view === "all" ? ACTIVE_BUTTON : BUTTON} onClick={() => selectView("all")} type="button">Toutes</button>
            <button aria-pressed={view === "todo"} className={view === "todo" ? ACTIVE_BUTTON : BUTTON} onClick={() => selectView("todo")} type="button">À traiter</button>
          </div>
        </div>
        <p className="m-0 text-[0.72rem] text-slate-600">Toutes les questions posées à Lumi, saisies ou envoyées par un bouton, apparaissent ici avec leur résultat. La liste s’actualise automatiquement. « À traiter » garde les questions sans réponse, en échec ou notées 👎. Les questions sont effacées après 90 jours.</p>
        {usage ? <p className="m-0 text-[0.72rem] font-semibold text-slate-700" data-epi-usage="true">{usage}</p> : null}
      </section>
      {error ? (
        <section className={SURFACE} role="alert">
          <p className="m-0 text-sm text-rose-700">{error}</p>
          <div><button className={BUTTON} onClick={() => void load()} type="button">Réessayer</button></div>
        </section>
      ) : loading && !items.length ? <p className="text-sm text-slate-600" role="status">Chargement des questions…</p>
        : groups.length ? groups.map((group) => (
          <section className="grid min-w-0 gap-2 rounded-[4px] border border-slate-200 bg-slate-50/90 p-2.5" key={group.activityDate || "unspecified"}>
            <h3 className="m-0 rounded-[4px] border border-slate-300 bg-slate-200/90 px-3 py-2 text-sm font-bold text-slate-950">{formatCaptureActivityGroupLabel(group.activityDate)}</h3>
            {group.items.map((item) => <EpiQuestionCard item={item} key={item.id} onResolve={resolve} resolving={resolvingId === item.id} />)}
          </section>
        )) : (
          <section className={SURFACE}>
            <p className="m-0 text-sm text-slate-600">{view === "todo" ? "Aucune question à traiter." : "Aucune question posée à Lumi pour le moment."}</p>
          </section>
        )}
      {total > PAGE_SIZE ? (
        <nav aria-label="Pagination des questions" className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-white px-4 py-3">
          <span className="text-xs text-slate-600">Page {page} sur {pageCount} · {total} questions</span>
          <div className="flex gap-2">
            <button className={BUTTON} disabled={loading || page <= 1} onClick={() => setPage((value) => value - 1)} type="button">Précédente</button>
            <button className={BUTTON} disabled={loading || !history?.has_more} onClick={() => setPage((value) => value + 1)} type="button">Suivante</button>
          </div>
        </nav>
      ) : null}
    </div>
  );
}
